import React from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Pause, Play, Settings } from "lucide-react";

export interface StatItem {
  label: string;
  value?: string | number;
  highlighted?: boolean;
}

export interface IntegrationDetailHeaderProps {
  title: string;
  iconInitials: string;
  iconBgClass?: string;
  status: string;
  version?: string;
  subtitlePrefix?: string;
  stats?: StatItem[];
  onRunNow?: () => void;
  onPause?: () => void;
  onConfigure?: () => void;
}

export const IntegrationDetailHeader: React.FC<
  IntegrationDetailHeaderProps
> = ({
  title,
  iconInitials,
  iconBgClass = "bg-gradient-to-r from-[#4F46E5] to-[#6366F1]",
  status,
  version = "v1.0.0",
  subtitlePrefix,
  stats = [],
  onRunNow,
  onPause,
  onConfigure,
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 bg-white border border-gray-200 rounded-2xl shadow-sm">
      {/* Left side: Icon, Title, Subtitle Stats */}
      <div className="flex items-start gap-4">
        {/* Initials Icon */}
        <div
          className={`w-14 h-14 ${iconBgClass} text-white font-extrabold text-xl flex items-center justify-center rounded-xl shadow-inner shrink-0`}
        >
          {iconInitials}
        </div>

        {/* Text Area */}
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              {title}
            </h1>

            {/* Status Badge */}
            <Badge
              className={`text-xs font-bold py-0.5 px-2.5 rounded-full border flex items-center gap-1.5 ${
                status
                  ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-50 border-gray-200"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  status ? "bg-emerald-500 animate-pulse" : "bg-gray-400"
                }`}
              />
              {status}
            </Badge>

            {/* Version Badge */}
            {version && (
              <Badge className="bg-gray-50 text-gray-600 hover:bg-gray-50 border border-gray-200 text-xs font-semibold py-0.5 px-2 rounded-full">
                {version}
              </Badge>
            )}
          </div>

          {/* Subtitle Stats */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500 font-medium">
            {subtitlePrefix && (
              <span className="text-gray-500">{subtitlePrefix}</span>
            )}
            {stats.map((stat, idx) => (
              <React.Fragment key={idx}>
                {(subtitlePrefix || idx > 0) && (
                  <span className="text-gray-300">·</span>
                )}
                <span className="flex items-center gap-1">
                  <span className="text-gray-500">{stat.label}</span>
                  {stat.value !== undefined && (
                    <span
                      className={`font-semibold ${
                        stat.highlighted
                          ? "text-emerald-600 font-bold"
                          : "text-gray-900"
                      }`}
                    >
                      {stat.value}
                    </span>
                  )}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Right side: Actions */}
      <div className="flex items-center gap-3 shrink-0">
        <Button variant="default" onClick={onConfigure}>
          <Settings className="w-4 h-4" />
          <span>Configure</span>
        </Button>
      </div>
    </div>
  );
};
